/**
 * Reveal on scroll, done once for the whole document.
 *
 * Everything that arrives — a grid cell, a section header — is hidden by CSS
 * only under `html.motion`, and main.tsx sets that class only when an
 * IntersectionObserver exists. So there is no configuration in which content
 * is hidden and nothing can show it: no observer means no class means nothing
 * was ever hidden. This hook is the half that does the showing.
 *
 * One observer for the page rather than one per component. Lazily loaded pages
 * and route changes add elements after mount, so a MutationObserver on #main
 * re-scans; observing an element twice is harmless, and each one is dropped
 * from the set the moment it has arrived.
 */
import { useEffect } from 'react';

/** Everything that has not yet arrived. */
const TARGETS = '.cell:not(.is-in), [data-reveal]:not(.is-in)';

export function useRevealOnScroll(): void {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const main = document.getElementById('main');
    if (!main) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      /*
       * Fire a little before the element's top edge clears the bottom of the
       * viewport, so the arrival is already under way when the eye gets there.
       * A low threshold, because a tall card would otherwise wait until most
       * of it was on screen.
       */
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
    );

    const scan = (): void => {
      main.querySelectorAll(TARGETS).forEach((element) => observer.observe(element));
    };
    scan();

    const mutations = new MutationObserver(scan);
    mutations.observe(main, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutations.disconnect();
    };
  }, []);
}
